import type { ComponentProps, ReactNode } from "react";

import { cn } from "../../lib/cn";

export interface FilterPillProps extends Omit<ComponentProps<"button">, "type"> {
	readonly active: boolean;
	readonly count?: number;
	/** A 7 px dot before the label in the colour of what the pill filters, as a full class ("bg-finance"). */
	readonly dotClassName?: string;
}

/** Toggle button for a list filter; `aria-pressed` exposes the state. */
export function FilterPill({ active, count, dotClassName, className, children, ...props }: FilterPillProps) {
	return (
		<button
			type="button"
			aria-pressed={active}
			className={cn(
				"inline-flex press cursor-pointer items-center gap-[5px] rounded-[18px] border px-3 py-[5px] font-sans text-caption transition-colors duration-[120ms]",
				active
					? "border-ink bg-ink text-bg"
					: "border-line bg-surface-2 text-ink-2 hover:bg-line hover:text-ink",
				className,
			)}
			{...props}
		>
			{dotClassName === undefined ? null : (
				<span aria-hidden="true" className={cn("h-[7px] w-[7px] shrink-0 rounded-full", dotClassName)} />
			)}
			{children}
			{count === undefined ? null : (
				<span className={cn("tabular-nums", active ? "opacity-70" : "text-ink-3")}>{count}</span>
			)}
		</button>
	);
}

export interface ToolbarProps {
	readonly children: ReactNode;
	/** Right-aligned content (a count or a search field). */
	readonly end?: ReactNode;
}

export function Toolbar({ children, end }: ToolbarProps) {
	return (
		<div className="mb-3.5 flex flex-wrap items-center gap-1.5">
			{children}
			{end === undefined ? null : <div className="ml-auto flex items-center gap-2">{end}</div>}
		</div>
	);
}
